import { m } from 'framer-motion';
import PropTypes from 'prop-types';
// @mui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
// assets
import { SeverErrorIllustration } from 'src/assets/illustrations';
// components
import { MotionContainer, varBounce } from 'src/components/animate';
import { isOfflineError } from 'src/hooks/use-offline';

// ----------------------------------------------------------------------

// Reusable network/offline error check from hook
const isConnectionError = (err) => isOfflineError(err);

export default function ErrorView({ error, onTryAgain, showTryAgain }) {
  const isConnection = isConnectionError(error);

  const getErrorMessage = () => {
    if (isConnection) return 'Lost connection';
    if (typeof error === 'string') return error;
    if (error?.data?.message) {
      const msg = error.data.message;
      if (Array.isArray(msg)) return msg.join(', ');
      return error?.status === 401 ? 'Session expired' : String(msg);
    }
    if (error?.error) return typeof error.error === 'string' ? error.error : 'Something went wrong';
    if (error instanceof Error) return error.toString();
    if (error?.name && error?.message) return `${error.name}: ${error.message}`;
    if (error?.message) return String(error.message);
    return 'Something went wrong';
  };

  const getSubMessage = () => {
    if (isConnection) {
      return 'Please check your internet connection and try again';
    }
    if (error?.status === 401) {
      return 'Please login again';
    }
    return 'Please try again later or contact the team';
  };

  return (
    <Box
      sx={{
        py: { xs: 2, sm: 3 },
        px: 2,
        m: 'auto',
        maxWidth: 480,
        width: '100%',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <MotionContainer>
        <m.div variants={varBounce().in}>
          <Typography variant="h4" sx={{ mb: 1 }}>
            {getErrorMessage()}
          </Typography>
        </m.div>

        <m.div variants={varBounce().in}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {getSubMessage()}
          </Typography>
        </m.div>

        <m.div variants={varBounce().in}>
          <SeverErrorIllustration sx={{ height: { xs: 180, sm: 220 }, my: 1 }} />
        </m.div>

        {(showTryAgain || onTryAgain) && (
          <m.div variants={varBounce().in}>
            <Button onClick={onTryAgain} size="large" variant="contained" sx={{ mt: 2 }}>
              Try Again
            </Button>
          </m.div>
        )}
      </MotionContainer>
    </Box>
  );
}

ErrorView.propTypes = {
  error: PropTypes.any,
  onTryAgain: PropTypes.func,
  showTryAgain: PropTypes.bool,
};
